import hash from 'object-hash'
import { TTLCache } from './ttl-cache'
import type {
  RequestUrl,
  RequestMethod,
  RequestOptions,
  RequestInterceptor,
  ResponseInterceptor,
  AuthFn,
  ApiOptions,
  PublicMethodInterface,
  ApiError
} from './api-types'
import { getApiBaseUrl } from '@/configs/environments/config'

const cache = new TTLCache({ debug: false })
const onRequestInterceptors = new Set<RequestInterceptor>()
const onResponseInterceptors = new Set<ResponseInterceptor>()

let authorization: AuthFn = _data => {}
let origin = ''

export const setOrigin = (value: string) => {
  origin = value
}

export const addRequestInterceptor = (fn: RequestInterceptor) => {
  onRequestInterceptors.add(fn)
  return () => removeRequestInterceptor(fn)
}
export const addResponseInterceptor = (fn: ResponseInterceptor) => {
  onResponseInterceptors.add(fn)
  return () => removeResponseInterceptor(fn)
}
export const removeRequestInterceptor = (fn: RequestInterceptor) =>
  onRequestInterceptors.delete(fn)
export const removeResponseInterceptor = (fn: ResponseInterceptor) =>
  onResponseInterceptors.delete(fn)

export const isApiError = <T>(err: Error): err is ApiError<T> =>
  'request' in err || 'response' in err

const getApiErrorStatus = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return null
  }

  const status = (error as { response?: { status?: number } }).response?.status

  return typeof status === 'number' ? status : null
}

export const getIsClientApiError = (error: unknown) => {
  const status = getApiErrorStatus(error)

  return status !== null && status >= 400 && status < 500
}

export const getIsNotFoundApiError = (error: unknown) =>
  getApiErrorStatus(error) === 404

export const useApiClient = async () => {
  const { hooks, $i18n } = useNuxtApp()
  const baseURL = await getApiBaseUrl()
  const $client = $fetch.create({ baseURL })

  async function request<T>(
    url: RequestUrl,
    method: RequestMethod,
    requestOptions: RequestOptions = {},
    apiOptions: ApiOptions = {}
  ): Promise<T> {
    const _apiOptions = Object.assign(
      { useAuthorization: true, cache: false },
      apiOptions
    )
    const language = $i18n.locale.value
    const id = hash({
      url,
      method,
      language,
      query: requestOptions.query
    })

    if (_apiOptions.cache && cache.has(id)) {
      return cache.get(id)
    }

    return $client<T>(
      url,
      Object.assign({}, requestOptions, {
        method,
        headers: Object.assign(
          {
            Referer: origin
          },
          requestOptions?.headers
        ),
        async onRequest({ request, options }) {
          ;(options.headers as Record<string, string>)['accept-language'] =
            language

          if (authorization && _apiOptions.useAuthorization) {
            await authorization({ request, options })
          }

          if (typeof requestOptions.onRequest === 'function') {
            await requestOptions.onRequest({ request, options })
          }

          onRequestInterceptors.forEach(fn => {
            fn({ id, request, options })
          })
        },
        async onResponse({ error, options, request, response }) {
          if (
            response.status >= 200 &&
            response.status < 399 &&
            _apiOptions.cache
          ) {
            cache.set(id, response._data, _apiOptions.cache)
          }

          if (typeof requestOptions.onResponse === 'function') {
            await requestOptions.onResponse({
              error,
              options,
              request,
              response
            })
          }

          onResponseInterceptors.forEach(fn => {
            fn({ id, error, options, request, response })
          })
        },
        async onResponseError(error) {
          // @ts-ignore
          hooks.callHook('api-client:error', error, id)
          return Promise.reject(error)
        }
      } satisfies RequestOptions)
    )
  }

  return {
    ...({
      get: (url, options?, apiOptions?) =>
        request(url, 'GET', options, apiOptions),

      post: (url, options?, apiOptions?) =>
        request(url, 'POST', options, apiOptions),

      put: (url, options?, apiOptions?) =>
        request(url, 'PUT', options, apiOptions),

      delete: (url, options?, apiOptions?) =>
        request(url, 'DELETE', options, apiOptions)
    } satisfies PublicMethodInterface),

    setAuthorization: (fn: AuthFn = _data => {}) => {
      authorization = fn
    }
  }
}
